/**
 * HorizontalScrollTabs - 水平滚动 Tab 导航
 *
 * 提供两种用法：
 * 1. useHorizontalTabScroll Hook（箭头与滚动区域布局分离时使用）
 * 2. HorizontalScrollTabs 组件（箭头与滚动区域在同一行时的便捷包装）
 *
 * 每个 tab 子元素需加 data-scroll-id={item.id} 属性，以便精确定位。
 */

import React, { useRef, useCallback } from 'react';
import cn from 'classnames';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// 内部工具函数
// ─────────────────────────────────────────────────────────────

/**
 * 将目标 tab 元素精确滚动到完全可见的位置。
 * 使用 getBoundingClientRect() 计算位置，避免 offsetLeft 受 offsetParent 影响导致的偏差。
 */
function scrollTabElementIntoView(container: HTMLDivElement, tabEl: HTMLElement, direction: 'left' | 'right') {
  const containerRect = container.getBoundingClientRect();
  const tabRect = tabEl.getBoundingClientRect();

  // tab 相对于滚动内容区域的绝对位置（与 scrollLeft 同坐标系）
  const tabContentLeft = tabRect.left - containerRect.left + container.scrollLeft;
  const tabContentRight = tabRect.right - containerRect.left + container.scrollLeft;

  if (direction === 'right' && tabContentRight > container.scrollLeft + container.clientWidth) {
    // tab 右侧被遮挡 → 滚动到 tab 右边恰好对齐容器右边
    container.scrollTo({ left: tabContentRight - container.clientWidth, behavior: 'smooth' });
  } else if (direction === 'left' && tabContentLeft < container.scrollLeft) {
    // tab 左侧被遮挡 → 滚动到 tab 左边恰好对齐容器左边
    container.scrollTo({ left: tabContentLeft, behavior: 'smooth' });
  }
}

// ─────────────────────────────────────────────────────────────
// Hook: useHorizontalTabScroll
// ─────────────────────────────────────────────────────────────

/**
 * 水平滚动 Tab 导航 Hook。
 *
 * @param items          - Tab 列表，每项必须包含 id 字段
 * @param activeId       - 当前激活的 Tab id
 * @param onActiveChange - 切换 Tab 的回调
 * @returns scrollRef    - 绑定到滚动容器元素的 ref
 * @returns navigate     - 调用 navigate('left'|'right') 切换到相邻 Tab 并精确滚动
 *
 * @example
 * const { scrollRef, navigate } = useHorizontalTabScroll(items, activeId, setActiveId);
 * // 滚动容器：<div ref={scrollRef}>...</div>
 * // 箭头按钮：<button onClick={() => navigate('left')} />
 * // 每个 tab 元素需加：data-scroll-id={item.id}
 */
export function useHorizontalTabScroll<T extends { id: string }>(
  items: T[],
  activeId: string,
  onActiveChange: (id: string) => void
) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const navigate = useCallback(
    (direction: 'left' | 'right') => {
      const idx = items.findIndex(item => item.id === activeId);
      if (idx === -1) return;
      const nextIdx = direction === 'left' ? idx - 1 : idx + 1;
      if (nextIdx < 0 || nextIdx >= items.length) return;

      const nextId = items[nextIdx].id;
      onActiveChange(nextId);

      // 等 React 完成状态更新、DOM 渲染后再计算位置
      setTimeout(() => {
        const container = scrollRef.current;
        if (!container) return;
        const tabEl = container.querySelector<HTMLElement>(`[data-scroll-id="${nextId}"]`);
        if (!tabEl) return;
        scrollTabElementIntoView(container, tabEl, direction);
      }, 0);
    },
    [items, activeId, onActiveChange]
  );

  return { scrollRef, navigate };
}

// ─────────────────────────────────────────────────────────────
// Component: HorizontalScrollTabs
// 适用于箭头与 tab 列表在同一行的场景
// ─────────────────────────────────────────────────────────────

export interface HorizontalScrollTabsProps {
  /** Tab 列表，每项必须有 id */
  items: Array<{ id: string }>;
  /** 当前激活的 Tab id */
  activeId: string;
  /** 切换激活 Tab 的回调 */
  onActiveChange: (id: string) => void;
  /**
   * Tab 内容，每个 tab 子元素需加 data-scroll-id={item.id} 属性
   * 以便组件内部精确定位目标元素
   */
  children: React.ReactNode;
  /** 是否显示箭头，默认 items.length > 1 时自动显示 */
  showArrows?: boolean;
  /** 整体外层容器 className */
  className?: string;
  /** 滚动内容区域 className */
  scrollContainerClassName?: string;
  /** 箭头按钮 className */
  arrowButtonClassName?: string;
}

const HorizontalScrollTabs: React.FC<HorizontalScrollTabsProps> = ({
  items,
  activeId,
  onActiveChange,
  children,
  showArrows,
  className,
  scrollContainerClassName,
  arrowButtonClassName,
}) => {
  const { scrollRef, navigate } = useHorizontalTabScroll(items, activeId, onActiveChange);
  const shouldShowArrows = showArrows ?? items.length > 1;

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {shouldShowArrows && (
        <button
          type="button"
          onClick={() => navigate('left')}
          className={cn('p-1 rounded hover:bg-[#F5F5F5] flex-shrink-0', arrowButtonClassName)}
        >
          <ChevronLeft size={20} />
        </button>
      )}

      <div
        ref={scrollRef}
        className={cn(
          'flex gap-2 overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-[#E5E5E5] pb-1',
          scrollContainerClassName
        )}
        style={{ scrollbarWidth: 'thin' }}
      >
        {children}
      </div>

      {shouldShowArrows && (
        <button
          type="button"
          onClick={() => navigate('right')}
          className={cn('p-1 rounded hover:bg-[#F5F5F5] flex-shrink-0', arrowButtonClassName)}
        >
          <ChevronRight size={20} />
        </button>
      )}
    </div>
  );
};

export default HorizontalScrollTabs;
